// "Aperçu" tab of the agent drawer (the default one, SPEC-UX-V03 §B): everything about the agent
// in one screen — what it does, the « Je valide avant d'agir » switch (saved at once, manage level;
// the others see the setting), its connectors with « Modifier les connecteurs » (one outside the
// viewer's « Connecteurs autorisés » marked « pas pour vos demandes »), its next three
// routines (active switch, LANCER) with « + Nouvelle routine » and « Toutes ses routines », its
// last three tasks with « Toutes ses tâches », and « Discuter ». The live status and the current
// task sit in the drawer header above every tab.
// Also the drawer's task feed: the agent's tasks (GET /api/tasks?agentId=…) for these last three
// and the « Tâches (n) » count.
import { h, button, switchToggle, stateBadge, taskState, emptyState, setText, replaceChildren } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, connectorLabel } from '../../format.js';
import { iconForConnector } from '../connectoricons.js';
import { canUseConnector } from '../connectorpolicy.js';
import { createRoutineList } from '../automations/routinelist.js';
import { taskTimeRef } from '../taskpanel.js';
import { latestTasks, NO_CONNECTOR_ENGINES, toolsSummary } from './model.js';
import { storeAgent } from './settings.js';
import './strings.js';

/** Tasks read for the feed (the same page as the Tâches tab). */
export const FEED_LIMIT = 100;

/**
 * The agent's tasks, loaded once and kept current with `task`, `task.removed` and `hello` events.
 * Without the list endpoint (404) it falls back to the tasks the browser already knows.
 * @param {object} ctx app context
 * @param {string} agentId
 * @returns {{readonly tasks: Map<string, object>, readonly status: 'loading'|'ok'|'unavailable'|'error',
 *   readonly capped: boolean, subscribe(fn: () => void): () => void, reload(): Promise<void>, destroy(): void}}
 */
export function createAgentTaskFeed(ctx, agentId) {
  const tasks = new Map();
  const subs = new Set();
  const offs = [];
  let status = 'loading';
  let capped = false;
  let seq = 0;
  let destroyed = false;
  const notify = () => { if (!destroyed) for (const fn of [...subs]) { try { fn(); } catch (err) { console.error('[agent tasks]', err); } } };

  function fromStore() {
    for (const tk of ctx.store.get().tasks?.values?.() || []) if (tk?.agentId === agentId && !tasks.has(tk.id)) tasks.set(tk.id, tk);
  }

  async function reload() {
    if (destroyed) return;
    const my = ++seq;
    try {
      const res = await ctx.api.get(ctx.api.withQuery('/api/tasks', { agentId, limit: FEED_LIMIT }));
      if (destroyed || my !== seq) return;
      const items = Array.isArray(res) ? res : Array.isArray(res?.tasks) ? res.tasks : [];
      tasks.clear();
      for (const tk of items) if (tk && typeof tk.id === 'string' && tk.agentId === agentId) tasks.set(tk.id, tk);
      capped = items.length >= FEED_LIMIT;
      status = 'ok';
    } catch (err) {
      if (destroyed || my !== seq) return;
      status = err?.status === 404 ? 'unavailable' : 'error';
      fromStore();
    }
    notify();
  }

  offs.push(ctx.events.on('task', (s) => {
    if (!s || typeof s.id !== 'string') return;
    if (s.agentId === agentId) tasks.set(s.id, s);
    else if (!tasks.delete(s.id)) return; // reassigned to another agent, or not ours
    notify();
  }));
  offs.push(ctx.events.on('task.removed', (d) => { if (d?.id && tasks.delete(d.id)) notify(); }));
  offs.push(ctx.events.on('hello', () => { reload(); }));
  reload();

  return {
    get tasks() { return tasks; },
    get status() { return status; },
    get capped() { return capped; },
    subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
    reload,
    destroy() {
      destroyed = true;
      seq += 1;
      subs.clear();
      for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
    },
  };
}

/**
 * Builds the tab.
 * @param {object} ctx app context
 * @param {object} initialAgent
 * @param {{routines: object, feed: ReturnType<typeof createAgentTaskFeed>, canManage?: boolean,
 *   canOperate?: boolean, onTab: (tab: string, opts?: {section?: string}) => void}} opts
 *   routines: the drawer's routine source (automations/routinelist.js createRoutineSource)
 * @returns {{el: HTMLElement, activate(): void, deactivate(): void, update(agent: object): void, destroy(): void}}
 */
export function createOverviewTab(ctx, initialAgent, { routines, feed, canManage = false, canOperate = false, onTab }) {
  let agent = initialAgent;
  let destroyed = false;
  let saving = false;
  const offs = [];
  const path = `/api/agents/${encodeURIComponent(agent.id)}`;

  const sec = (label, ...children) => h('section', { class: 'ad-ov-sec', 'aria-label': label },
    h('h3', { class: 'ad-set-title mono-label' }, label), ...children);

  // --- what it does ------------------------------------------------------------------------------
  const doesEl = h('p', { class: 'ad-ov-does' });
  // « Discuter » only for who may write to the agent (the Discussion tab is read-only otherwise).
  const chatBtn = canOperate
    ? button(t('ad.ov.chat'), { variant: 'secondary', size: 'sm', icon: 'send', class: 'ad-ov-chat', onClick: () => onTab('chat') })
    : null;

  // --- autonomy ------------------------------------------------------------------------------------
  const autonomyHint = h('p', { class: 'ad-ov-hint' });
  const autonomySwitch = switchToggle(t('ad.autonomy.switch'), {
    checked: agent.autonomy !== 'auto',
    disabled: !canManage,
    onChange: (v) => setAutonomy(v ? 'approve' : 'auto'),
  });
  autonomySwitch.classList.add('ad-ov-autonomy');
  const autonomyNote = canManage ? null : h('p', { class: 'ad-ov-note muted' }, t('ad.ov.autonomyReadOnly'));

  function syncAutonomy() {
    const approve = agent.autonomy !== 'auto';
    if (!saving) autonomySwitch.input.checked = approve;
    setText(autonomyHint, t(`ad.f.autonomyHint.${approve ? 'approve' : 'auto'}`));
    autonomyHint.classList.toggle('is-auto', !approve);
  }

  async function setAutonomy(autonomy) {
    if (saving || destroyed || !canManage) return;
    saving = true;
    autonomySwitch.input.disabled = true;
    try {
      const res = await ctx.api.patch(path, { autonomy });
      if (destroyed) return;
      const next = res && typeof res === 'object' && res.id === agent.id ? { ...agent, ...res } : { ...agent, autonomy };
      agent = next;
      storeAgent(ctx, next);
      ctx.toast?.success?.(t(autonomy === 'auto' ? 'ad.ov.autonomySavedAuto' : 'ad.ov.autonomySavedApprove', { name: next.name || '' }));
    } catch (err) {
      if (destroyed) return;
      ctx.toast?.error?.(err);
    } finally {
      saving = false;
      if (!destroyed) {
        autonomySwitch.input.disabled = !canManage;
        syncAutonomy();
      }
    }
  }

  // --- connectors ------------------------------------------------------------------------------------
  const connList = h('ul', { class: 'ad-ov-conns' });
  const connEdit = canManage
    ? button(t('ad.ov.editConnectors'), { variant: 'ghost', size: 'sm', icon: 'settings', class: 'ad-ov-editconn', onClick: () => onTab('settings', { section: 'connectors' }) })
    : null;
  let connKey = '';

  function renderConnectors() {
    const state = ctx.store.get();
    const known = state.connectors || [];
    const grants = Array.isArray(agent.connectors) ? agent.connectors.filter((c) => c && typeof c.server === 'string' && c.server) : [];
    const key = JSON.stringify([agent.engine, grants, known.map((c) => [c.key, c.name, c.status]), state.me?.role, state.me?.connectorPolicy ?? null]);
    if (key === connKey) return;
    connKey = key;
    if (NO_CONNECTOR_ENGINES.includes(agent.engine)) {
      replaceChildren(connList, h('li', { class: 'ad-ov-empty muted' }, t(agent.engine === 'gemini' ? 'ad.conn.noEngineGemini' : 'ad.conn.noEngine')));
      return;
    }
    if (!grants.length) {
      replaceChildren(connList, h('li', { class: 'ad-ov-empty muted' }, t('ad.ov.noConnectors')));
      return;
    }
    replaceChildren(connList, ...grants.map((g) => {
      const server = known.find((c) => c.key === g.server) || { key: g.server, name: g.server };
      const name = connectorLabel(server.name || server.key);
      const down = !!server.status && server.status !== 'connected' && server.builtin !== true;
      const missing = !known.some((c) => c.key === g.server);
      // Outside my connectors: the agent works without it for my requests.
      const denied = server.builtin !== true && !canUseConnector(state, g.server);
      return h('li', { class: ['ad-ov-conn', { 'is-down': down || missing, 'is-denied': denied }], dataset: { conn: g.server } },
        iconForConnector(server, { size: 24 }),
        h('span', { class: 'ad-ov-conn-name' }, name,
          // A tool selection (SPEC-UX-V03 §A.3) shows as « k sur N outils ».
          Array.isArray(g.tools) ? h('span', { class: 'ad-ov-conn-tools muted' }, ` · ${toolsSummary(g.tools, Number.isFinite(server.toolCount) ? server.toolCount
            : Number.isFinite(server.readCount) || Number.isFinite(server.writeCount) ? (server.readCount || 0) + (server.writeCount || 0) : null)}`) : null),
        h('span', { class: 'ad-ov-conn-access mono-label' }, g.access === 'write' ? t('ad.conn.write') : t('ad.conn.read')),
        down || missing ? h('span', { class: 'ad-ov-conn-warn' }, missing ? t('ad.ov.connMissing') : t('ad.ov.connDown')) : null,
        denied ? h('span', { class: 'ad-ov-conn-warn ad-ov-conn-denied', title: t('ad.ov.connDeniedTitle') }, t('ad.ov.connDenied')) : null);
    }));
  }

  // --- routines ------------------------------------------------------------------------------------
  const newRoutineBtn = button(t('ad.ov.newRoutine'), { variant: 'secondary', size: 'sm', icon: 'plus', class: 'ad-ov-newroutine', onClick: () => routineList?.create() });
  const allRoutinesBtn = button(t('ad.ov.allRoutines'), { variant: 'ghost', size: 'sm', iconRight: 'arrowRight', class: 'ad-ov-allroutines', onClick: () => onTab('routines') });
  let routineList = null;
  routineList = createRoutineList(ctx, { agentId: agent.id, source: routines, limit: 3, compact: true, onChange: (l) => syncRoutineButtons(l) });

  function syncRoutineButtons(list = routineList) {
    if (!list) return;
    newRoutineBtn.hidden = !list.canCreate();
    const n = routines.status === 'ok' ? routines.items.length : null;
    setText(allRoutinesBtn.querySelector('.btn-label'), n ? t('ad.ov.allRoutinesN', { n }) : t('ad.ov.allRoutines'));
    allRoutinesBtn.hidden = routines.status === 'ok' && n === 0;
  }

  // --- tasks -----------------------------------------------------------------------------------------
  const taskList = h('ul', { class: 'ad-ov-tasks' });
  const allTasksBtn = button(t('ad.ov.allTasks'), { variant: 'ghost', size: 'sm', iconRight: 'arrowRight', class: 'ad-ov-alltasks', onClick: () => onTab('tasks') });

  function renderTasks() {
    if (feed.status === 'loading') {
      replaceChildren(taskList, h('li', { class: 'ad-ov-empty muted' }, t('ad.tasks.loading')));
      return;
    }
    const last = latestTasks(feed.tasks.values(), agent.id, 3);
    const now = Date.now();
    if (!last.length) {
      replaceChildren(taskList, h('li', { class: 'ad-ov-empty muted' }, t('ad.ov.noTasks', { name: agent.name })));
      allTasksBtn.hidden = true;
      return;
    }
    allTasksBtn.hidden = false;
    replaceChildren(taskList, ...last.map((tk) => {
      const title = tk.title || t('task.untitled');
      return h('li', { class: 'ad-ov-task' }, h('button', {
        type: 'button', class: 'ad-ov-task-btn', dataset: { id: tk.id },
        'aria-label': t('ad.tasks.open', { title: `${title} — ${t(`state.${taskState(tk)}`)}` }),
        onClick: () => ctx.navigate(`#/task/${encodeURIComponent(tk.id)}`),
      }, stateBadge(tk), h('span', { class: 'ad-ov-task-title' }, title),
      h('span', { class: 'ad-ov-task-time muted' }, relativeTime(taskTimeRef(tk) || tk.createdAt, now, { short: true }))));
    }));
  }

  // --- layout ----------------------------------------------------------------------------------------
  const el = h('div', { class: 'ad-overview' },
    h('div', { class: 'ad-ov-head' }, doesEl, chatBtn),
    sec(t('ad.ov.autonomy'), autonomySwitch, autonomyHint, autonomyNote),
    sec(t('ad.ov.connectors'), connList, connEdit),
    sec(t('ad.ov.routines'), routineList.el, h('div', { class: 'ad-ov-actions' }, newRoutineBtn, allRoutinesBtn)),
    sec(t('ad.ov.tasks'), taskList, h('div', { class: 'ad-ov-actions' }, allTasksBtn)));

  function render() {
    if (destroyed) return;
    setText(doesEl, agent.does || t('ad.ov.noDoes'));
    doesEl.classList.toggle('muted', !agent.does);
    syncAutonomy();
    renderConnectors();
    syncRoutineButtons();
    renderTasks();
  }

  offs.push(feed.subscribe(renderTasks));
  if (ctx.store.on) offs.push(ctx.store.on('connectors', renderConnectors), ctx.store.on('me', renderConnectors));
  render();

  return {
    el,
    activate() { render(); },
    deactivate() {},
    /** The agent changed (rename, autonomy, connectors saved elsewhere). */
    update(next) {
      if (!next || destroyed) return;
      agent = { ...agent, ...next };
      render();
    },
    destroy() {
      destroyed = true;
      for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
      routineList.destroy();
    },
  };
}
